import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as approvalInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { TGetPendingGoldConversionFiltersRedux } from '~types/feat/gold';

import * as Styles from './_goldConversionApprovalTableSearchAndFilterBar.module.scss';
import dayjs from 'dayjs';
import { MetalTypeEnum } from '~utils/enums';

const GoldConversionApprovalSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.approval.pendingGoldConversionListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const initialFilters = approvalInitialState.pendingGoldConversionListing.filters;
    const productList = useAppSelector((state) => state.gold.allProducts);
    const setFilters = (inputFilters: TGetPendingGoldConversionFiltersRedux) => dispatch(combinedActions.approvalSetGoldConversionListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingGoldConversionListingAttempt());
    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
        });
        dispatch(combinedActions.approvalGetPendingGoldConversionListingAttempt());
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    useEffect(() => {
        return () => {
            // clear filters on unmount
            handleCancelClick();
        };
    }, []);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];
        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const massagedProductsListOption = () => {
        if (!productList.length) return [];
        return productList.map((product) => ({
            label: product.productName,
            value: product.productId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Product Name:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedProductsListOption().sort((a, b) => a.value - b.value),
                value: filters.productName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    productName: event.target.value,
                    productId: productList.find((product) => product.productName === event.target.value)?.productId || 0,
                }),
                optionSelected: !!productList.find((product) => product.productName === filters.productName),
                noOptionsMessage: 'No product found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom || '',
                setDate: (date: string) => {
                    if (filters.dateTo && date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo || '',
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default GoldConversionApprovalSearchAndFilterBar;
